import React, {useCallback, useEffect, useMemo, useState} from "react";
import Header from "components/Header";
import DeliverableGroup from "components/DeliverableGroup";
import {useFeatureLane} from "data/access";
import {useSurveySectionsRenderingModel} from "data/access/survey-sections-rendering-model";
import Loading from "components/Loading";
import {assertNoError} from "helpers/asserts";
import BrandStylesDeliverable from "components/BrandStylesDeliverable";
import {Button, Tooltip} from "flowbite-react";
import {surveyQuestionPath} from "hooks/page-navigation/survey";
import {useNavigate} from "react-router-dom";
import {isNullOrUndefined} from "helpers/is-null-or-undefined";
import {useBrandVoice} from "data/access/deliverables/useBrandVoice";
import {useBrandStyles} from "data/access/deliverables/useBrandStyles";
import {CheckoutForm} from "components/CheckoutForm";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowsRotate, faCircleInfo} from "@fortawesome/free-solid-svg-icons";
import {httpsCallable} from "firebase/functions";
import {functions} from "hooks/firebase";
import {PushToEmailListRequest} from "data/models/shared/push-to-email-list";
import {getAuth} from "firebase/auth";
import {useAuthState} from "react-firebase-hooks/auth";
import {deepEqual} from "helpers/deep-equal";


export type DashboardSurveyStatusKind = "finished" | "in progress" | "not started";


const DashboardPage = () => {
  const auth = getAuth();
  const [user, userLoading, userError] = useAuthState(auth);
  const [surveySectionsRenderingModel, surveySectionsRenderingModelLoading, surveySectionsRenderingModelError] = useSurveySectionsRenderingModel();

  const [
    generateBrandVoice,
    {
      brandVoice,
      availableCreditsToUse: availableBrandVoiceCreditsToUse,
      canGenerateNew: canGenerateNewBrandVoice,
    }, brandVoiceDefinitionLoading, brandVoiceDefinitionError] = useBrandVoice();
  const [generateBrandStyles,
    {
      brandStyles,
      availableCreditsToUse: availableBrandStylesCreditsToUse,
      canGenerateNew: canGenerateNewBrandStyles,
    }, brandStylesDefinitionLoading, brandStylesDefinitionError] = useBrandStyles();
  const isGenerated = (brandVoice !== null && brandStyles !== null);
  const canGenerateNew = canGenerateNewBrandVoice || canGenerateNewBrandStyles;

  const [paymentRequested, setPaymentRequested] = useState(false);

  const regenerate = useCallback(() => {
    if (availableBrandVoiceCreditsToUse > 0 && availableBrandStylesCreditsToUse > 0) {
      void generateBrandVoice();
      void generateBrandStyles();
    } else {
      setPaymentRequested(true);
    }
  }, [generateBrandVoice, generateBrandStyles]);

  const navigate = useNavigate();

  const [
    canvaAssetsFeatureLanes,
    canvaAssetsFeatureLanesLoading,
    canvaAssetstFeatureLanesError
  ] = useFeatureLane("canva-assets");

  const surveyStatus: DashboardSurveyStatusKind | null = useMemo(() => {
    if (canvaAssetsFeatureLanes === null) {
      return null;
    }

    if (canvaAssetsFeatureLanes
        .reduce((finishedStatus, featureLane) => finishedStatus && featureLane.userSurveys
                .reduce(
                    (finishedStatus, survey) =>
                        finishedStatus && survey.status === "finished", true)
            , true)
    ) {
      return "finished";
    }

    if (isNullOrUndefined(canvaAssetsFeatureLanes
        .find((featureLane) => featureLane.userSurveys
            .find(
                (survey) =>
                    survey.status === "finished")
        )
    )) {
      return "not started";
    }

    return "in progress";
  }, [canvaAssetsFeatureLanes]);

  useEffect(() => {
    if (
        !brandVoiceDefinitionLoading
        && !brandStylesDefinitionLoading
        && canGenerateNew
        && availableBrandVoiceCreditsToUse > 0
        && availableBrandStylesCreditsToUse > 0
        && surveyStatus === "finished"
        && !isGenerated) {
      regenerate();
    }
  }, [
    brandVoiceDefinitionLoading,
    brandStylesDefinitionLoading,
    canGenerateNew,
    surveyStatus,
    isGenerated
  ]);

  useEffect(() => {
    if (
        isNullOrUndefined(user) || user.email === null || userLoading || !isNullOrUndefined(userError)
        || isNullOrUndefined(surveySectionsRenderingModel) || surveySectionsRenderingModelLoading || surveySectionsRenderingModelError !== null
        || surveyStatus === null
    ) {
      return;
    }
    const payload: PushToEmailListRequest = {
      recipient: {
        email: user.email,
        name: surveySectionsRenderingModel.userLogin.name,
      },
      data: {
        Company: surveySectionsRenderingModel.businessName,
        Finishedquestions: surveyStatus === "finished" ? "yes" : "no",
        Paid: availableBrandVoiceCreditsToUse !== 0 && availableBrandStylesCreditsToUse !== 0 ? "Express" : "no",
      }
    };

    const storedEmailListPayload = localStorage.getItem("emailListPayload");
    if (storedEmailListPayload !== null) {
      const storedEmailListPayloadParsed = JSON.parse(storedEmailListPayload);
      if (deepEqual(storedEmailListPayloadParsed, payload)) {
        return;
      }
    }
    localStorage.setItem("emailListPayload", JSON.stringify(payload));
    const putSubscriberToEmailListFunction = httpsCallable<PushToEmailListRequest, void>(functions, "putSubscriberToEmailList");
    // Keep the console.log to avoid the promise being cancelled (I guess).
    putSubscriberToEmailListFunction(payload).then(() => console.log("Data updated."));
  }, [
    user, userLoading, userError,
    surveySectionsRenderingModel, surveySectionsRenderingModelLoading, surveySectionsRenderingModelError,
    surveyStatus,
    availableBrandVoiceCreditsToUse,
    availableBrandStylesCreditsToUse,
  ]);

  const {title, description} = useMemo(() => {
    switch (surveyStatus) {
      case "finished":
        return {
          title: null,
          description: null,
        };
      default: {
        const forBusinessName = surveySectionsRenderingModel !== null ? ` for ${surveySectionsRenderingModel.businessName}` : '';
        return {
          title: `Build brand styles${forBusinessName}`,
          description: null,
        };
      }
    }
  }, [surveyStatus]);

  const showPaymentForm = !brandVoiceDefinitionLoading && !brandStylesDefinitionLoading && (
      paymentRequested ||
      (availableBrandVoiceCreditsToUse === 0
      || availableBrandStylesCreditsToUse === 0)
      && surveyStatus === "finished"
      && !isGenerated
  );

  const showRegenerateButton = useMemo(() => (
      !paymentRequested
      && canGenerateNew
      && surveyStatus === "finished"
      && isGenerated
  ), [paymentRequested, canGenerateNew, availableBrandVoiceCreditsToUse, availableBrandStylesCreditsToUse, surveyStatus, isGenerated]);

  assertNoError(
      canvaAssetstFeatureLanesError
      ?? surveySectionsRenderingModelError
      ?? brandVoiceDefinitionError
      ?? brandStylesDefinitionError
  );

  if (
      canvaAssetsFeatureLanesLoading
      || surveySectionsRenderingModelLoading
  ) {
    return <>
      <Header saveFormCallback={null} />
      <Loading className={"mt-48 text-xl"} loadingText={"Loading Dashboard ..."}/>
    </>
  }
  if (canvaAssetsFeatureLanes === null || surveyStatus === null) {
    return <Header saveFormCallback={null} />
  }

  function navigateNextSurvey() {
    if (canvaAssetsFeatureLanes === null) {
      return;
    }
    const featureLaneInProgress = canvaAssetsFeatureLanes.find(featureLane => featureLane.userSurveys.find(survey => survey.status === "in progress")) ?? null;
    if (featureLaneInProgress === null) {
      return;
    }
    const surveyInProgress = featureLaneInProgress.userSurveys.find(survey => survey.status === "in progress") ?? null;
    if (surveyInProgress === null) {
      return;
    }
    navigate(surveyQuestionPath(
        "canva-assets",
        featureLaneInProgress.id,
        surveyInProgress.id,
        "first",
    ));
  }

  function navigateToBrandAssets(download: boolean) {
    if (surveyStatus !== "finished") {
      return;
    }
    if (isGenerated) {
      navigate(`${surveyQuestionPath(
          "deliverables",
          "canva-assets",
          "previews",
          "brand-styles",
      )}${download ? "?download" : ""}`);
    }
  }

  const tooltipContent = <>
    <p>Your recommendations include eight type styles:</p>
    <p>Title, Subtitle, Heading, Subheading, Section header, Body, Quote and Caption styles, </p>
    <p>plus color recommendations for primary, secondary, background and accent colors</p>
    <p>and brand voice guidelines.</p>
  </>;

  return (
      <>
        <Header saveFormCallback={null} />
        <div className="mx-auto items-center">
          <div className={"flex flex-col items-center"}>
            <div className={"grow mb-4"}>
              <DeliverableGroup
                  id={"canva-assets"}
                  title={title}
                  description={description}
                  featureLanes={canvaAssetsFeatureLanes}
              />
            </div>
            {["in progress", "not started"].includes(surveyStatus) &&
                <div className={"flex flex-col items-center max-w-screen-sm"}>
                    <div className={"text-center"}>
                    <span className={"text-neutral-500 text-base font-normal"}>
                        Tell us about your business and get recommendations on colors and fonts,
                        words and messages. Then you can download your brand styles.
                    </span>
                    </div>
                    <div className={"my-8"}>
                        <Button
                            className={"min-w-24 ml-1 rounded-full"}
                            color={"blue"}
                            onClick={navigateNextSurvey}
                        >{surveyStatus === "in progress" ? "Continue" : "Start"}</Button>
                    </div>
                </div>}
            {surveyStatus === "finished" &&
                <div className={"flex flex-col gap-8 max-w-screen-sm text-center"}>
                    <div>
                        <span className={"text-neutral-500 text-base font-normal"}>Click on a circle above to edit your answers.</span>
                    </div>
                    <div className={"flex flex-col items-center"}>
                        <Tooltip content={tooltipContent}
                                 className={"text-left max-w-screen-sm"}
                        >
                            <div className={"flex flex-row"}>
                                <div className={"flex flex-col"}>
                                    <span
                                        className={"text-3xl font-bold"}>Your Brand Style Guide is ready!</span>
                                  {showPaymentForm && <span
                                        className={"text-lg text-neutral-500"}>Annual license includes unlimited edits and downloads.</span>}
                                </div>
                                <div className={"md:ml-2 ml-0 mr-2 md:mr-0"} ><FontAwesomeIcon color={"gray"} size={"xl"}
                                                                         icon={faCircleInfo}/></div>
                            </div>
                        </Tooltip>
                    </div>
                </div>
            }
            {showPaymentForm && <div className={"flex flex-col w-full max-w-screen-sm items-center"}>
                <div className={"text-xl mt-4 text-bc-blue underline"} ><a href={"https://survey.alchemer.com/s3/8460388/BC-BrandKit-In-App-Feedback"} target={"_blank"}>Get $5</a></div>
                <CheckoutForm/>
              {paymentRequested && <div className={"flex flex-row mt-8"}>
                  <Button
                      className={"min-w-24 mr-1 rounded-full"}
                      color={"gray"}
                      onClick={() => setPaymentRequested(false)}
                  >Cancel</Button>
              </div>}
            </div>}
            {showRegenerateButton && <div className={"flex flex-col gap-8 w-full mt-12 max-w-screen-sm items-center"}>
                <div>
                    You have made changes to your answers since you generated the Brand Styles last time.
                </div>
                <Button
                    className={"ml-1 px-8 mb-12 rounded-full"}
                    color={"blue"}
                    onClick={() => regenerate()}
                >
                    <span className={"text-xl font-bold"}>Regenerate Brand Styles</span>
                    <FontAwesomeIcon icon={faArrowsRotate} className={"ml-4 text-white"} size={"2xl"}/>
                </Button>
            </div>}
            <div
                className={"flex flex-row mt-12 px-2 md:px-12 w-full max-w-screen-md justify-center"}
            >
              <BrandStylesDeliverable
                  brandStyles={brandStyles}
                  businessName={surveySectionsRenderingModel?.businessName ?? null}
                  navigateToBrandAssets={navigateToBrandAssets}
                  brandVoice={brandVoice?.voice ?? null}
                  brandVoiceLoading={brandVoiceDefinitionLoading}
                  alternativeText={
                      "A customized summary and directions for how to write in your brand voice." +
                      " This is perfect to copy and paste into Canva’s brand kit to help you with your writing."
                  }
              />
            </div>
          </div>
        </div>
      </>
  );
}

export default DashboardPage;
